import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/shop", festival: "Diwali", is_active: true };

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    const { data } = await api.get("/banners", { params: { active_only: false } });
    setBanners(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(b) {
    setEditing(b.id);
    setForm({ title: b.title, subtitle: b.subtitle, cta_text: b.cta_text, cta_link: b.cta_link, festival: b.festival, is_active: b.is_active });
    setOpen(true);
  }
  async function save() {
    try {
      if (editing) await api.put(`/banners/${editing}`, form);
      else await api.post("/banners", form);
      toast.success(editing ? "Banner updated" : "Banner created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this banner?")) return;
    try { await api.delete(`/banners/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl text-[#2C1810]">Festival Banners</h1>
          <p className="font-body text-[#5B3D2A]">Diwali, Holi, Raksha Bandhan promotions.</p>
        </div>
        <Button data-testid="admin-add-banner-btn" onClick={openNew} className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white btn-lift">
          <Plus className="w-4 h-4 mr-2" /> New banner
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {banners.length === 0 && (
          <div className="md:col-span-2 p-10 bg-white/60 rounded-3xl border border-dashed border-[#FFD1A6] text-center font-body text-[#5B3D2A]">
            No banners yet. Add your first festive promotion.
          </div>
        )}
        {banners.map((b) => (
          <div key={b.id} data-testid={`banner-${b.id}`} className="bg-gradient-to-br from-[#4A148C] to-[#2C0855] rounded-2xl p-6 text-[#FFF9F2] shadow-warm">
            <div className="flex items-start justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[#FFCF52]">{b.festival}</div>
              <div className={`text-[10px] px-2 py-1 rounded-full ${b.is_active ? "bg-[#0F7B3B]" : "bg-gray-500"}`}>{b.is_active ? "Live" : "Draft"}</div>
            </div>
            <div className="font-display text-2xl mt-2">{b.title}</div>
            <div className="text-sm font-body text-[#FFCF52]/85 mt-1">{b.subtitle}</div>
            <div className="text-xs font-body mt-3 text-[#FFCF52]/70">CTA: {b.cta_text} → {b.cta_link}</div>
            <div className="mt-4 flex gap-2">
              <button data-testid={`banner-edit-${b.id}`} onClick={() => openEdit(b)} className="rounded-full bg-[#E65C00] px-4 py-1.5 text-xs text-white flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
              <button data-testid={`banner-delete-${b.id}`} onClick={() => remove(b.id)} className="rounded-full bg-[#B71C1C] px-4 py-1.5 text-xs text-white flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit banner" : "New banner"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Festival</Label><Input data-testid="banner-festival" value={form.festival} onChange={(e) => setForm({ ...form, festival: e.target.value })} /></div>
            <div><Label>Title</Label><Input data-testid="banner-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Subtitle</Label><Textarea data-testid="banner-sub" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CTA text</Label><Input data-testid="banner-cta-text" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} /></div>
              <div><Label>CTA link</Label><Input data-testid="banner-cta-link" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="banner-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <span className="font-body text-sm">Active on storefront</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="banner-save" onClick={save} className="bg-[#E65C00] hover:bg-[#C44A00] text-white rounded-full">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
